import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { usePagination } from '@web/app/shared';
import { type RouteContext, createNavigator, extractRouteParams } from '@web/app/shared/utils/route-utils';

import { usePrompt, usePrompts } from '@codes/agents';
import { deleteUndefinedProperty } from '@codes/utils';

interface MyPromptsParams extends RouteContext {
    id?: string;
}

export const useMyPrompts = () => {
    const { t } = useTranslation();
    const routeParams = extractRouteParams<MyPromptsParams>(useParams());
    const { id: promptId, workspaceId, projectId } = routeParams;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const navigator = createNavigator(navigate, { workspaceId, projectId });

    const isCreateMode = searchParams.get('mode') === 'create';
    const searchQuery = searchParams.get('search') || '';
    const page = parseInt(searchParams.get('page') || '0', 10);
    const limit = 10;
    const stereo = (searchParams.get('stereo') as 'all' | 'system' | 'user') || '';

    const { data: promptsData, isLoading: isPromptsLoading } = usePrompts({
        parentId: '',
        ...deleteUndefinedProperty({ view: 'mine', page, limit, stereo }),
    });
    const { data: selectedPrompt, isLoading: isPromptLoading } = usePrompt(promptId || '');

    const { paginationRange } = usePagination({
        totalCount: promptsData?.total || 0,
        pageSize: limit,
        currentPage: page,
        siblingCount: 1,
    });

    const isFormVisible = Boolean(promptId) || isCreateMode;
    const formMode = promptId ? 'edit' : 'create';

    const handlePromptSelect = (selectedPromptId: string) => {
        const currentParams = new URLSearchParams(searchParams);
        navigator.navigateToWithQuery(`/prompts/${selectedPromptId}`, currentParams);
    };

    const handleCreateNew = (defaultType?: 'system' | 'user') => {
        const currentParams = new URLSearchParams(searchParams);
        currentParams.set('mode', 'create');
        if (defaultType) {
            currentParams.set('type', defaultType);
        }
        navigator.navigateToWithQuery('/prompts', currentParams);
    };

    const handleFormSuccess = (newPromptId: string) => {
        if (isCreateMode) {
            const currentParams = new URLSearchParams(searchParams);
            currentParams.delete('mode'); // create 모드는 제거
            navigator.navigateToWithQuery(`/prompts/${newPromptId}`, currentParams);
        }
    };

    const handlePageChange = (newPage: number) => {
        setSearchParams(prev => {
            prev.set('page', newPage.toString());
            return prev;
        });
    };

    const handleSearchChange = (query: string) => {
        setSearchParams(prev => {
            if (query) {
                prev.set('search', query);
            } else {
                prev.delete('search');
            }
            prev.set('page', '0');
            return prev;
        });
    };

    const handleStereoChange = (newStereo: 'all' | 'system' | 'user') => {
        setSearchParams(prev => {
            if (newStereo === 'all') {
                prev.delete('stereo');
            } else {
                prev.set('stereo', newStereo);
            }
            prev.set('page', '0');
            return prev;
        });
    };

    return {
        promptId,
        isCreateMode,
        searchQuery,
        page,
        limit,
        stereo,
        promptsData,
        isPromptsLoading,
        selectedPrompt,
        isPromptLoading,
        paginationRange: paginationRange || [],
        isFormVisible,
        formMode,
        handlePromptSelect,
        handleCreateNew,
        handleFormSuccess,
        handlePageChange,
        handleSearchChange,
        handleStereoChange,
    };
};
